import { Card, CardContent, Icon, styled, Typography } from '@mui/material'
import React from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useNavigate } from 'react-router-dom-v5-compat'

import AccountForm from '@/app/module/auth/component/common/account'
import ProfileForm from '@/app/module/auth/component/common/profile'
import {
  AccountFormValues,
  InvitationProfileFormValues,
  InvitationValues,
} from '@/app/module/auth/component/common/specs'
import { useAcceptInvitationSignup } from '@/app/module/auth/hooks/use-accept-invitation-signup'

import { Container, Content } from './common'
import { InvitationResponse } from './types'

type Props = {
  invitationResponse: InvitationResponse
  invitationToken: string
}

const LoggedOutNew: React.FC<Props> = ({ invitationResponse, invitationToken }) => {
  const navigate = useNavigate()
  const [step, setStep] = React.useState(0)
  const [formData, setFormData] = React.useState<Partial<InvitationValues>>({})
  const [captchaDisplayed, setCaptchaDisplayed] = React.useState(false)

  const [acceptInvitationSignup, { isLoading }] = useAcceptInvitationSignup()

  const handleProfileSubmit = (data: InvitationProfileFormValues) => {
    setFormData((s) => ({ ...s, ...data }))
    setStep(1)
  }

  const handleAccountSubmit = (data: AccountFormValues) => {
    setFormData((s) => ({ ...s, ...data }))
    setCaptchaDisplayed(true)
  }

  const handleAccountBack = (data: AccountFormValues) => {
    setFormData((s) => ({ ...s, ...data }))
    setStep(0)
  }

  const handleCaptchaVerify = async (verifyToken: string) => {
    setCaptchaDisplayed(false)

    const success = await acceptInvitationSignup({
      invitationToken,
      email: invitationResponse.email,
      password: formData.password || '',
      phone: formData.phone || '',
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      country: formData.country || '',
      verifyToken,
    })

    if (!success) {
      return
    }

    navigate('/dashboard')
  }

  return (
    <Container>
      <Content>
        <Typography className="invitation-message-text" variant="h5" textAlign="center">
          You have been invited to the organization {invitationResponse.orgName} ({invitationResponse.orgId}).
          <br />
          If you want to join that organization, you have to sign up first.
        </Typography>

        <StyledCard>
          <CardContent>
            <IconContainer>
              <StyledIcon>person_add</StyledIcon>
            </IconContainer>
            {step === 0 && (
              <ProfileForm
                mode="invitation"
                data={{
                  firstName: invitationResponse.firstName,
                  lastName: invitationResponse.lastName,
                  ...formData,
                }}
                onSubmit={handleProfileSubmit}
              />
            )}
            {step === 1 && (
              <AccountForm
                data={formData}
                email={invitationResponse.email}
                isLoading={isLoading}
                onBack={handleAccountBack}
                onSubmit={handleAccountSubmit}
              />
            )}
          </CardContent>

          {captchaDisplayed && <GoogleReCaptcha onVerify={handleCaptchaVerify} />}
        </StyledCard>
      </Content>
    </Container>
  )
}

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: '2rem',
  marginBottom: '3rem',
  overflow: 'visible',
})

const IconContainer = styled(CardContent)({
  textAlign: 'center',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

export default LoggedOutNew
